import * as React from "react";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { FETCH_INSTALLATION_LEVEL_READINGS, FETCH_TANKS_LEVEL_READINGS } from "../../../Fedx";
import { InstallationLevelReadings } from "../../../core/models/InstallationLevelReadings";
import BaseChart from "../../../components/Charts/BaseChart";
import {
  buildColoredAxisTitle,
  buildHTMLLabelForThreshold,
  buildTimeAreaChartOptions,
  chartThresholds,
} from "../../../helpers/chartHelpers";
import { isArrayValid, isNumberValid } from "../../../helpers/utility";
import { Skeleton } from "@material-ui/lab";
import {
  EVENT_INSTALLATION_SPLIT_LEVEL_TANKS_CHANGE,
  EVENT_INSTALLATION_LEVEL_FILTER_DATE_CHANGE,
  EVENT_REFRESH_INSTALLATION_DETAIL_PAGE,
  EVENT_DARK_MODE,
} from "../../../Events";
import {
  makeStyles,
  createStyles,
  Theme,
  Grid,
  useTheme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import clsx from "clsx";
import { useHubEventDateWindow } from "../../../core/hooks/useHubHooks";
import { TanksLevelsReadings } from "../../../core/models/TanksLevelsReadings";
import TankModel from "../../../core/models/Tank";
import { ITankLevel } from "../../../core/contracts/ITankLevel";
import { TimeseriesData } from "../../../core/models/TimeSeriesData";
import { InstallationLevelServiceFetchParams } from "../../../services/InstallationLevelService";
import { get } from "lodash";
import useAuth from "../../../core/hooks/useAuth";
import { IInstallationLevelReadings } from "../../../core/contracts/IInstallationLevel";
import useConstructor from "../../../core/hooks/useConstructor";
import { LocalCache } from "../../../helpers/cache";
import { dateFromTimezonePtBr } from "../../../helpers/datetime";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "height": "100%",

      "& > *": {
        height: "100%",
      },
    },

    skeletonContainer: {
      rowGap: theme.spacing(2),
    },

    tankChartWrapper: {
      position: "relative",
    },

    tankInfoWrapper: {
      "display": "flex",
      "columnGap": theme.spacing(2),
      "align-items": "center",
      "justify-content": "center",
      "background": theme.customPaper.background.cardBackgroundLightContrastWhite,

      "& > div": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },

      "& .label": {
        fontSize: "0.825rem",
      },

      "& .value": {
        fontSize: "1.75rem",
        color: theme.palette.primary.main,
        lineHeight: "0.8",

        [theme.breakpoints.down("xs")]: {
          fontSize: "0.75rem",
        },
      },
    },

    hidden: {
      display: "none",
    },
  })
);

type InstallationLevelTabProps = {
  installationId: string;
  hasMoreThanOneTank?: boolean;
  tanks?: TankModel[];
};

function InstallationLevelTab({
  installationId,
  hasMoreThanOneTank,
  tanks,
}: InstallationLevelTabProps) {
  const [installationLoading, setInstallationLoading] = React.useState(false);
  const [tanksLoading, setTanksLoading] = React.useState(false);
  const [showTanks, setShowTanks] = React.useState(false);
  const [
    installationLevelData,
    setInstallationLevelData,
  ] = React.useState<null | InstallationLevelReadings>(null);
  const [tanksLevelData, setTanksLevelData] = React.useState<null | TanksLevelsReadings>(null);
  const currentDateWindow = useHubEventDateWindow(
    EVENT_INSTALLATION_LEVEL_FILTER_DATE_CHANGE,
    true
  );

  const [isDark, setIsDark] = React.useState<boolean>(false);

  const lcache = new LocalCache();

  const getFromCache = () => {
    const prefersDark = lcache.get<boolean>("prefersDarkMode") || false;
    setIsDark(prefersDark);
  };

  useConstructor(async () => {
    getFromCache();
  });

  useHubXSubscribe(EVENT_DARK_MODE, (isDark: boolean) => {
    setIsDark(isDark);
  });

  const authProvider = useAuth();
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreenWidth = useMediaQuery(theme.breakpoints.down("xs"));
  const hub = useHubx();
  useHubXSubscribe(EVENT_INSTALLATION_SPLIT_LEVEL_TANKS_CHANGE, setShowTanks);

  const serieName = React.useMemo(() => {
    const firstPosWithUOM =
      installationLevelData?.readings?.find(
        (e) => e.installation_pressure_uom && e.installation_pressure_uom != ""
      ) || undefined;
    const uom = get(firstPosWithUOM, `installation_pressure_uom`);
    return `Pressão ${uom ? "(" + uom + ")" : ""}`;
  }, [installationLevelData]);

  function buildTanksCharts(tanksReadings: TanksLevelsReadings) {
    const buildTankChart = (reading: ITankLevel) => {
      const tankData: TimeseriesData[] =
        reading?.readings?.map((data) => {
          return [dateFromTimezonePtBr(data.reading_date), data.tank_level_su];
        }) || [];

      const tankPressure: TimeseriesData[] =
        reading?.readings?.map((data) => {
          return [dateFromTimezonePtBr(data.reading_date), data.tank_pressure || 0]; //data.installation_pressure
        }) || [];

      const yThresholds: chartThresholds[] = [];

      const firstPosWithUOM = (reading?.readings?.find(
        (e) => e.tank_pressure_uom && e.tank_pressure_uom != ""
      ) as unknown) as IInstallationLevelReadings;
      const uom = get(firstPosWithUOM, `tank_pressure_uom`);
      const serieNameByTank = `Pressão ${uom ? "(" + uom + ")" : ""}`;

      if (reading.tank_capacity_su) {
        yThresholds.push({
          value: reading.tank_capacity_su,
          color: theme.palette.primary.main,
          id: `tank_${reading.tank_id}_max`,
          HTMLLabel: buildHTMLLabelForThreshold(
            "Capacidade",
            reading.tank_capacity_su,
            reading.product_uom
          ),
        });
      }

      if (reading?.outage_level) {
        yThresholds.push({
          value: reading.outage_level,
          color: theme.palette.error.main,
          id: `tank_${reading.tank_id}_outage`,
          HTMLLabel: buildHTMLLabelForThreshold(
            "Nível de Lastro",
            reading.outage_level,
            reading.product_uom
          ),
        });
      }

      return (
        <Grid
          className={classes.tankChartWrapper}
          lg={6}
          xs={12}
          item
          key={`tank_${reading.tank_id}`}
        >
          <div className={classes.tankInfoWrapper}>
            <div>
              <Typography className="label">Serial RTU</Typography>
              <Typography className="value">{reading.rtu_serialnumber}</Typography>
            </div>
            <div>
              <Typography className="label">Id Tanque</Typography>
              <Typography className="value">{reading.tank_id}</Typography>
            </div>
          </div>
          <BaseChart
            allowChartUpdate
            options={buildTimeAreaChartOptions(
              tankData,
              "",
              "Nível",
              buildColoredAxisTitle(
                `Nível${reading.product_uom ? ` (${reading.product_uom})` : ""}`
              ),
              yThresholds,
              undefined,
              isMobileScreenWidth ? false : true,
              false,
              true,
              undefined,
              authProvider.hasHighLevel() && tankPressure?.length
                ? (tankPressure as TimeseriesData[])
                : undefined,
              authProvider.hasHighLevel() && tankPressure.length ? serieNameByTank : undefined,
              authProvider.hasHighLevel() && tankPressure.length ? serieNameByTank : undefined,
              isDark,
              true
            )}
          />
        </Grid>
      );
    };

    return (
      <Grid container spacing={2} direction="row" justify="center" alignItems="center">
        {tanksReadings.map((tankReading) => buildTankChart(tankReading))}
      </Grid>
    );
  }

  const fetchData = React.useCallback(async () => {
    setInstallationLoading(true);
    try {
      const fetchParams: InstallationLevelServiceFetchParams = {
        installation_id: installationId,
        startDate: currentDateWindow?.start ?? undefined,
        endDate: currentDateWindow?.end ?? undefined,
      };
      if (fetchParams.startDate && fetchParams.endDate) {
        const results: InstallationLevelReadings = await hub.request(
          FETCH_INSTALLATION_LEVEL_READINGS,
          fetchParams
        );
        setInstallationLevelData(results);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("InstallationLevelTab → fetchData → error", error);
    } finally {
      setInstallationLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateWindow, installationId]);

  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationId, currentDateWindow]);

  const fetchTanksData = React.useCallback(async () => {
    setTanksLoading(true);
    if (currentDateWindow?.start && currentDateWindow?.end) {
      const results: TanksLevelsReadings = await hub.request(FETCH_TANKS_LEVEL_READINGS, {
        tanks_ids: tanks?.map((tank) => tank.tank_id),
        startDate: currentDateWindow?.start,
        endDate: currentDateWindow?.end,
      });
      setTanksLevelData(results);
      setTanksLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateWindow, tanks]);

  React.useEffect(() => {
    if (!hasMoreThanOneTank || !isArrayValid(tanks)) {
      setShowTanks(false);
      return;
    }

    fetchTanksData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateWindow, hasMoreThanOneTank, tanks]);

  const doRefreshData = () => {
    fetchData();
    fetchTanksData();
  };

  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, doRefreshData);

  const buildDataForChart: () => [Date, number][] | undefined = React.useCallback(() => {
    if (isArrayValid(installationLevelData?.readings)) {
      return installationLevelData?.readings?.map((data) => {
        return [dateFromTimezonePtBr(data.reading_date), data.installation_level_su];
      });
    }
  }, [installationLevelData]);

  const buildDataSerie2ForChart: () => [Date, number][] | undefined = React.useCallback(() => {
    if (isArrayValid(installationLevelData?.readings)) {
      return installationLevelData?.readings?.map((data) => {
        return [dateFromTimezonePtBr(data.reading_date), data.installation_pressure || 0]; // data.installation_pressure
      });
    }
    return [];
  }, [installationLevelData]);

  const chartData = React.useMemo(() => buildDataForChart(), [buildDataForChart]);
  const chartData2 = React.useMemo(() => buildDataSerie2ForChart(), [buildDataSerie2ForChart]);

  const chartThresholdsY = React.useMemo(() => {
    const ret = [];

    if (isNumberValid(installationLevelData?.outage_level)) {
      ret.push({
        id: `${installationLevelData?.installation_id}_very_low_threshold`,
        value: installationLevelData?.outage_level ?? 0,
        color: theme.customPalette.levels.veryLow,
        HTMLLabel: buildHTMLLabelForThreshold(
          "Nível de Lastro",
          installationLevelData?.outage_level,
          installationLevelData?.product_uom
        ),
      });
    }

    if (isNumberValid(installationLevelData?.reserve_level)) {
      ret.push({
        id: `${installationLevelData?.installation_id}_low_threshold`,
        value: installationLevelData?.reserve_level ?? 0,
        color: theme.palette.text.secondary,
        HTMLLabel: buildHTMLLabelForThreshold(
          "Estoque de Segurança",
          installationLevelData?.reserve_level,
          installationLevelData?.product_uom
        ),
      });
    }

    if (isNumberValid(installationLevelData?.installation_capacity_su)) {
      ret.push({
        id: `${installationLevelData?.installation_id}_capacity_threshold`,
        value: installationLevelData?.installation_capacity_su ?? 0,
        color: theme.customPalette.levels.normal,
        HTMLLabel: buildHTMLLabelForThreshold(
          "Capacidade",
          installationLevelData?.installation_capacity_su,
          installationLevelData?.product_uom
        ),
      });
    }

    return ret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationLevelData]);
  const chartThresholdX = React.useMemo(() => {
    return installationLevelData?.trips?.map((trip) => {
      return {
        id: `${installationLevelData?.installation_id}_delivery_${trip.trip_id}`,
        value: new Date(trip.trip_date).getTime(),
        color: theme.palette.text.secondary,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationLevelData]);

  const chartOptions = chartData
    ? buildTimeAreaChartOptions(
        chartData,
        "",
        "Nível",
        installationLevelData?.product_uom
          ? `Nível (${installationLevelData.product_uom})`
          : "Nível",
        chartThresholdsY,
        chartThresholdX,
        isMobileScreenWidth ? false : true,
        false,
        true,
        undefined,
        authProvider.hasHighLevel() && chartData2?.length
          ? (chartData2 as TimeseriesData[])
          : undefined,
        authProvider.hasHighLevel() && chartData2?.length ? serieName : undefined,
        authProvider.hasHighLevel() && chartData2?.length ? serieName : undefined,
        isDark,
        true
      )
    : undefined;
  const numberOfTanks = tanks?.length || 4;

  const buildTanksLoading = React.useCallback(() => {
    const emptyTanks = new Array(numberOfTanks).fill({});

    return (
      <Grid
        className={classes.skeletonContainer}
        container
        spacing={2}
        justify="center"
        alignItems="center"
      >
        {emptyTanks.map((_, idx) => (
          <Grid item key={`tank_chart_${idx}`}>
            <Skeleton width={isMobileScreenWidth ? 300 : 600} height={400} />
          </Grid>
        ))}
      </Grid>
    );
  }, [classes.skeletonContainer, isMobileScreenWidth, numberOfTanks]);

  return (
    <>
      <div className={clsx(classes.root, showTanks && classes.hidden)}>
        {installationLoading ? <Skeleton /> : <BaseChart options={chartOptions} allowChartUpdate />}
      </div>
      <div className={clsx(classes.root, !showTanks && classes.hidden)}>
        {tanksLoading
          ? buildTanksLoading()
          : tanksLevelData
          ? buildTanksCharts(tanksLevelData)
          : null}
      </div>
    </>
  );
}

export default InstallationLevelTab;
